import React from 'react';
import { Button, LinearProgress, Typography } from '@material-ui/core';
import { Link } from 'react-router-dom';

import { CommunityEntityType } from 'generated/graphql';
import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';
import CommunitiesPageBanner from 'assets/pageBanners/communities.avif';
import { lighterYellow } from 'utils/color-styles';

import { MetricsData } from './types';

type Props = {
  entityType: CommunityEntityType;
  hasAccess: boolean;
  metrics?: MetricsData;
};

const dollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const Stat = ({ header, children }: { header: string; children: React.ReactNode }) => (
  <div>
    <Typography>{header}</Typography>
    {children}
  </div>
);

const Metrics = ({
  metrics,
  entityType,
}: {
  metrics: MetricsData;
  entityType: CommunityEntityType;
}) => {
  const percentMembers =
    metrics.employeesCount && metrics.employeesCount > 0
      ? Math.round((100 * metrics.totalMembers) / metrics.employeesCount)
      : null;

  const styleBig = { fontWeight: 400, fontSize: '32px' };
  const styleSmall = { fontSize: '10px' };
  const styleStatColum: React.CSSProperties = {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  };
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '15px 10px',
        flex: '1 1 auto',
        justifyContent: 'flex-start',
      }}
    >
      <div style={styleStatColum}>
        <Stat header="Total members">
          <Typography style={styleBig}>{metrics.totalMembers}</Typography>
        </Stat>
        {percentMembers !== null && (
          <Stat header="Membership breakdown">
            <Typography style={styleSmall}>
              <strong>{percentMembers}%</strong> members
              <br />
              <strong>{100 - percentMembers}%</strong> non members
            </Typography>
          </Stat>
        )}
      </div>
      <div style={styleStatColum}>
        <Stat header="Total Volunteer Hours">
          <Typography style={styleBig}>{Math.round(metrics.totalVolunteering / 60)}</Typography>
        </Stat>
        <Stat
          header={`Total ${
            entityType === CommunityEntityType.Team ? 'Team' : 'Community'
          } Donations`}
        >
          <Typography style={styleBig}>{dollarFormatter.format(metrics.totalDonations)}</Typography>
        </Stat>
      </div>
    </div>
  );
};

function getCreatePath(entityType: CommunityEntityType): string {
  return entityType === CommunityEntityType.Team ? '/teams/new' : '/communities/new';
}

function getCreateButtonText(entityType: CommunityEntityType): string {
  return entityType === CommunityEntityType.Team ? 'Create Team' : 'Create Community';
}

function getEntityTitle(entityType: CommunityEntityType): string {
  return entityType === CommunityEntityType.Team ? 'Teams' : 'Communities';
}

function getEntityDescription(entityType: CommunityEntityType): string {
  if (entityType === CommunityEntityType.Team) {
    return 'Teams let you group employees by department, location, or ERG to track participation and enable friendly competition in challenges.';
  }
  return 'Communities are for your affinity groups, employee resource groups (ERGs), and business resource groups (BRGs). Here, members can meet around opportunities, share content, and foster an inclusive program.';
}

export const EntityBanner = ({ entityType, hasAccess, metrics }: Props) => (
  <>
    <PageBannerContainer
      color={lighterYellow}
      style={{ height: 'inherit', marginBottom: 0, position: 'relative', minHeight: '200px' }}
    >
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'row-reverse',
          flexWrap: 'wrap',
          flex: '1 1 auto',
          marginTop: 40,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            flex: '0 1 auto',
            justifyContent: 'flex-end',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSfnaPwwQk3Ms5FuCxDYcE_ymkTaCAl_hogGk7MLYjcc2Mx2TA/viewform?usp=sf_link"
              target="_blank"
            >
              <Button color="primary" variant="outlined" size="large">
                <span style={{ paddingRight: 8 }}>Feedback</span>
              </Button>
            </a>
            {hasAccess ? (
              <Link to={getCreatePath(entityType)}>
                <Button color="primary" variant="outlined" size="large">
                  {getCreateButtonText(entityType)}
                </Button>
              </Link>
            ) : (
              <LinearProgress />
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            flex: '1 1 auto',
          }}
        >
          <h1 style={{ margin: 0, flex: '1 1 auto' }}>{getEntityTitle(entityType)}</h1>
          {metrics && <Metrics metrics={metrics} entityType={entityType} />}
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 108,
          backgroundImage: `url(${CommunitiesPageBanner})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right bottom',
          backgroundSize: 'contain',
        }}
      />
    </PageBannerContainer>
    <Typography style={{ marginTop: 20 }}>{getEntityDescription(entityType)}</Typography>
  </>
);
